import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  useFaenas,
  useCrearFaena,
  useActualizarFaena,
  useEliminarFaena
} from '../../hooks/useReferencias';
import { CrudTable, CrudForm, fechaColumn } from '../../components/admin/crud';
import {
  codigoField,
  descripcionField,
  ubicacionField,
  fechaInicioField,
  fechaFinField
} from '../../components/admin/crud/CrudForm';
import type { Faena } from '../../types';
import type { CrearFaena } from '../../services/referenciasService';

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const FaenasView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // ✅ PERMISOS UAU - Control granular de botones
  const canCreate = hasPermission('faenas.crear');
  const canEdit = hasPermission('faenas.editar');
  const canDelete = hasPermission('faenas.eliminar');

  // Estado local
  const [filtros, setFiltros] = useState({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: Faena;
  }>({
    isOpen: false,
    mode: 'create',
  });

  // Hooks de datos
  const { data, isLoading, error } = useFaenas(filtros);
  const crearMutation = useCrearFaena();
  const actualizarMutation = useActualizarFaena();
  const eliminarMutation = useEliminarFaena();

  // ===============================
  // CONFIGURACIÓN DE COLUMNAS
  // ===============================

  const columns = useMemo(() => [
    {
      key: 'codigo',
      title: t('common.code'),
      width: '120px',
    },
    {
      key: 'descripcion',
      title: t('common.description'),
      width: '200px',
    },
    {
      key: 'ubicacion',
      title: t('common.location'),
      width: '160px',
      render: (ubicacion: string) => ubicacion || '-',
    },
    fechaColumn<Faena>('fecha_inicio', t('common.start_date')),
    fechaColumn<Faena>('fecha_fin', t('common.end_date')),
    {
      key: 'estado',
      title: t('common.status'),
      width: '100px',
      render: (estado: string) => {
        const getLabel = () => {
          switch (estado) {
            case 'activa': return t('admin.status_active');
            case 'pausada': return t('admin.status_paused');
            case 'finalizada': return t('admin.status_finished');
            default: return estado;
          }
        };

        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            estado === 'activa'
              ? 'bg-green-100 text-green-800'
              : estado === 'pausada'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {getLabel()}
          </span>
        );
      },
    },
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DE FILTROS
  // ===============================

  const filters = useMemo(() => [
    {
      key: 'search',
      label: t('admin.search_placeholder'),
      type: 'text' as const,
      placeholder: t('admin.search_placeholder'),
    },
    {
      key: 'ubicacion',
      label: t('common.location'),
      type: 'text' as const,
      placeholder: t('admin.filter_by_location'),
    },
    {
      key: 'estado',
      label: t('common.status'),
      type: 'select' as const,
      options: [
        { value: 'activa', label: t('admin.status_active') },
        { value: 'pausada', label: t('admin.status_paused') },
        { value: 'finalizada', label: t('admin.status_finished') },
      ],
    },
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DE FORMULARIO
  // ===============================

  const formFields = useMemo(() => [
    codigoField,
    descripcionField,
    ubicacionField,
    fechaInicioField,
    fechaFinField,
    {
      name: 'estado',
      label: t('common.status'),
      type: 'select' as const,
      required: true,
      options: [
        { value: 'activa', label: t('admin.status_active') },
        { value: 'pausada', label: t('admin.status_paused') },
        { value: 'finalizada', label: t('admin.status_finished') },
      ],
    },
  ], [t]);

  // ===============================
  // MANEJADORES DE EVENTOS
  // ===============================

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('errors.no_permission'));
      return;
    }
    setFormModal({
      isOpen: true,
      mode: 'create',
    });
  };

  const handleEdit = (faena: Faena) => {
    setFormModal({
      isOpen: true,
      mode: 'edit',
      data: faena,
    });
  };

  const handleDelete = async (faena: Faena) => {
    try {
      await eliminarMutation.mutateAsync(faena.id);
      toast.success(t('admin.delete_success'));
    } catch (error) {
      toast.error(t('errors.delete_error'));
      console.error('Error eliminando faena:', error);
    }
  };

  const handleFormSubmit = async (datos: CrearFaena) => {
    try {
      if (formModal.mode === 'create') {
        await crearMutation.mutateAsync(datos);
        toast.success(t('admin.create_success'));
      } else if (formModal.data) {
        await actualizarMutation.mutateAsync({
          id: formModal.data.id,
          datos,
        });
        toast.success(t('admin.update_success'));
      }

      setFormModal({ isOpen: false, mode: 'create' });
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario faena:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  // ===============================
  // ESTADO DE CARGA DEL FORMULARIO
  // ===============================

  const isFormLoading = crearMutation.isLoading || actualizarMutation.isLoading;

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button
          onClick={() => navigate('/admin')}
          className="hover:text-gray-700 transition-colors"
        >
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.faenas')}</span>
      </div>

      {/* Botón volver */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      {/* Tabla CRUD */}
      <CrudTable
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title={t('admin.faenas_title')}
        addButtonText={t('admin.add')}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Modal de formulario */}
      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={formFields}
        title={formModal.mode === 'create' ? t('admin.create_faena') : t('admin.edit_item')}
        submitText={formModal.mode === 'create' ? t('common.save') : t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default FaenasView;
